import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { APP_CONFIG, AppConfig } from 'src/app.config';
import { TrackingContextService } from './tracking-context.service';
import { CORRELATION_ID, SKIP_TRACKING, generateCorrelationId } from './tracking-tokens';
import {
    ApiCallEvent,
    TrackingBatchPayload,
    TrackingEvent,
    UserActionEvent,
} from './tracking.model';

// Re-exported for callers/tests that import tokens via the service.
export { SKIP_TRACKING, CORRELATION_ID } from './tracking-tokens';

/** Fields callers supply for a user action; context + envelope added here. */
export type UserActionInput = Pick<UserActionEvent, 'name' | 'metadata'> & { correlationId: string };

/** Fields callers/interceptor supply for an API call; context added here. */
export type ApiCallInput = Omit<
    ApiCallEvent,
    'eventType' | 'timestamp' | 'app' | 'env' | 'user' | 'sessionId' | 'contactId'
>;

const DEFAULT_BATCH_SIZE = 20;
const DEFAULT_FLUSH_INTERVAL_MS = 10_000;
const DEFAULT_MAX_BUFFER_SIZE = 200;

/**
 * Core tracking pipeline (PRODSUPP-145765).
 *
 * Accepts user-action and api-call events, enriches each with ambient context
 * (app, env, user, sessionId, contactId), buffers them, and flushes batches to
 * the Policy360Orchestrator `policy360-audit` action by size threshold or on a
 * timer. The pipeline is fire-and-forget: it is disabled by config and all
 * failures are swallowed so tracking never breaks an app flow.
 *
 * On page unload the remaining buffer is flushed synchronously via
 * `navigator.sendBeacon`, which survives teardown where an async XHR would be
 * cancelled. The buffer is bounded (drop-oldest) so it cannot grow without
 * limit when the backend is unavailable.
 */
@Injectable({ providedIn: 'root' })
export class TrackingService implements OnDestroy {
    private buffer: TrackingEvent[] = [];
    private flushTimer: ReturnType<typeof setInterval> | null = null;
    private pageHideHandler: (() => void) | null = null;
    private visibilityHandler: (() => void) | null = null;

    private readonly enabled: boolean;
    private readonly batchSize: number;
    private readonly flushIntervalMs: number;
    private readonly maxBufferSize: number;

    constructor(
        private http: HttpClient,
        private context: TrackingContextService,
        @Inject(APP_CONFIG) private config: AppConfig
    ) {
        this.enabled = !!this.config.trackingEnabled && !!this.config.trackingIngestUrl;
        this.batchSize = this.config.trackingBatchSize ?? DEFAULT_BATCH_SIZE;
        this.flushIntervalMs = this.config.trackingFlushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
        this.maxBufferSize = this.config.trackingMaxBufferSize ?? DEFAULT_MAX_BUFFER_SIZE;

        if (this.enabled) {
            this.startTimer();
            this.registerUnloadFlush();
        }
    }

    /** True when tracking is configured and turned on. */
    isEnabled(): boolean {
        return this.enabled;
    }

    /** Generates a fresh correlation id for a new user action. */
    newCorrelationId(): string {
        return generateCorrelationId();
    }

    /**
     * Builds an HttpContext that stamps the given correlation id onto a request
     * so the logging interceptor can tie the resulting api_call to a prior
     * user action. Pass the returned context as `{ context }` on the call.
     */
    withCorrelation(correlationId: string): HttpContext {
        return new HttpContext().set(CORRELATION_ID, correlationId);
    }

    /** Records a user action event. No-op when tracking is disabled. */
    trackUserAction(input: UserActionInput): void {
        if (!this.enabled) return;
        try {
            const event: UserActionEvent = {
                eventType: 'user_action',
                name: input.name,
                metadata: input.metadata,
                correlationId: input.correlationId,
                ...this.envelope(),
            };
            this.enqueue(event);
        } catch {
            // Never let tracking break the caller.
        }
    }

    /** Records an API call event. No-op when tracking is disabled. */
    trackApiCall(input: ApiCallInput): void {
        if (!this.enabled) return;
        try {
            const event: ApiCallEvent = {
                eventType: 'api_call',
                ...input,
                ...this.envelope(),
            };
            this.enqueue(event);
        } catch {
            // Never let tracking break the caller.
        }
    }

    /**
     * Flushes the current buffer to the ingest endpoint. Fire-and-forget: a
     * failed flush does not throw and does not drop the already-dequeued batch
     * silently beyond logging to the console.
     */
    flush(): void {
        if (!this.enabled || this.buffer.length === 0) return;

        const batch = this.buffer;
        this.buffer = [];

        try {
            const payload: TrackingBatchPayload = { action: 'policy360-audit', events: batch };
            const headers = new HttpHeaders({
                'Content-Type': 'application/json',
                ...(this.config.trackingApiKey ? { 'x-api-key': this.config.trackingApiKey } : {}),
            });

            this.http
                .post(this.config.trackingIngestUrl as string, payload, {
                    headers,
                    context: new HttpContext().set(SKIP_TRACKING, true),
                })
                .subscribe({
                    next: () => undefined,
                    error: () => undefined, // swallow — tracking must not surface errors
                });
        } catch {
            // Swallow synchronous failures too.
        }
    }

    /**
     * Synchronously ships the remaining buffer with `navigator.sendBeacon` so
     * events survive page unload (an async POST would be cancelled). Returns
     * true when a beacon was queued.
     *
     * sendBeacon cannot set custom headers, so the api key is passed as a
     * `apiKey` query parameter; the `policy360-audit` handler must accept the
     * key via `x-api-key` header OR `apiKey` query param. The payload is sent
     * as an application/json Blob.
     */
    flushViaBeacon(): boolean {
        if (!this.enabled || this.buffer.length === 0) return false;
        if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
            // No beacon support: fall back to the async flush.
            this.flush();
            return false;
        }

        const batch = this.buffer;
        this.buffer = [];

        try {
            const payload: TrackingBatchPayload = { action: 'policy360-audit', events: batch };
            const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
            const url = this.beaconUrl();
            const queued = navigator.sendBeacon(url, blob);
            if (!queued) {
                // Browser refused (e.g. payload too large): restore for a later flush.
                this.buffer = batch.concat(this.buffer);
            }
            return queued;
        } catch {
            // Restore the batch so nothing is silently lost.
            this.buffer = batch.concat(this.buffer);
            return false;
        }
    }

    ngOnDestroy(): void {
        this.stopTimer();
        this.unregisterUnloadFlush();
        // Prefer a beacon on teardown; falls back to async flush internally.
        this.flushViaBeacon();
    }

    /** Appends the api key as a query param since beacons can't set headers. */
    private beaconUrl(): string {
        const base = this.config.trackingIngestUrl as string;
        const key = this.config.trackingApiKey;
        if (!key) return base;
        return base + (base.includes('?') ? '&' : '?') + 'apiKey=' + encodeURIComponent(key);
    }

    /** Registers pagehide + visibility listeners that beacon-flush the buffer. */
    private registerUnloadFlush(): void {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;

        // pagehide covers navigations/closes; visibilitychange covers tab
        // switches and mobile backgrounding (where pagehide is unreliable).
        this.pageHideHandler = () => this.flushViaBeacon();
        this.visibilityHandler = () => {
            if (document.visibilityState === 'hidden') {
                this.flushViaBeacon();
            }
        };

        window.addEventListener('pagehide', this.pageHideHandler);
        document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    private unregisterUnloadFlush(): void {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        if (this.pageHideHandler) {
            window.removeEventListener('pagehide', this.pageHideHandler);
            this.pageHideHandler = null;
        }
        if (this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
            this.visibilityHandler = null;
        }
    }

    /** Adds the ambient context + timestamp shared by every event. */
    private envelope() {
        const ctx = this.context.resolve();
        return {
            timestamp: new Date().toISOString(),
            app: ctx.app,
            env: ctx.env,
            user: ctx.user,
            sessionId: ctx.sessionId,
            contactId: ctx.contactId,
        };
    }

    /** Buffers an event, trims to the cap, and flushes when the batch is full. */
    private enqueue(event: TrackingEvent): void {
        this.buffer.push(event);

        // Bounded buffer: drop oldest beyond the cap so memory can't grow
        // unbounded if the backend is unavailable.
        if (this.buffer.length > this.maxBufferSize) {
            this.buffer.splice(0, this.buffer.length - this.maxBufferSize);
        }

        if (this.buffer.length >= this.batchSize) {
            this.flush();
        }
    }

    private startTimer(): void {
        if (this.flushTimer !== null) return;
        this.flushTimer = setInterval(() => this.flush(), this.flushIntervalMs);
    }

    private stopTimer(): void {
        if (this.flushTimer !== null) {
            clearInterval(this.flushTimer);
            this.flushTimer = null;
        }
    }
}
